import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import Icon from '../../components/AppIcon';
import NavigationHeader from '../homepage/components/NavigationHeader';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EcologicalZoneCard from './components/EcologicalZoneCard';
import ZoneDetailModal from './components/ZoneDetailModal';
import HabitatComparisonTool from './components/HabitatComparisonTool';
import SeasonalTimeline from './components/SeasonalTimeline';
import VirtualTourCard from './components/VirtualTourCard';
import ConservationProgressCard from './components/ConservationProgressCard';
import FilterPanel from './components/FilterPanel';

const SiteShowcaseGallery = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [activeView, setActiveView] = useState('grid');
  const [showComparison, setShowComparison] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    season: 'all',
    accessibility: 'all',
    conservationStatus: [],
    features: []
  });

  // Add new showcase categories section before ecologicalZones data
  const showcaseCategories = [
  {
    id: 'tourism',
    title: 'Tourism Activities',
    description: 'Eco-tourism experiences featuring paddle boats and river cruises',
    image: "/assets/images/UNDERGROUND_-_6-1769151502955.jpg",
    imageAlt: 'Tourists in orange life vests approaching the underground river entrance through emerald-green waters surrounded by pristine tropical forest, showcasing organized eco-tourism experience',
    icon: 'Ship',
    path: '/tourism-activities-hub',
    highlights: ['Paddle boat adventures', 'River cruise tours', 'Guided expeditions']
  }];

  // Mock Data - Ecological Zones
  const ecologicalZones = [];

  // Mock Data - Virtual Tours
  const virtualTours = [
  {
    id: 'tour-1',
    title: 'Philippine Eagle Territory 360°',
    description: 'Experience the majestic habitat of the critically endangered Philippine Eagle through immersive 360° photography.',
    thumbnail: "https://images.unsplash.com/photo-1534136179765-fdf782dd98d3",
    thumbnailAlt: 'Aerial view of dense rainforest canopy with emergent trees where Philippine Eagles nest',
    duration: '12 min',
    locations: 8,
    views: '15.2K',
    highlights: ['Eagle Nest Sites', 'Canopy Views', 'Audio Guide']
  },
  {
    id: 'tour-2',
    title: 'Mangrove Ecosystem Journey',
    description: 'Navigate through the intricate mangrove channels and discover the nursery of the sea.',
    thumbnail: "https://images.unsplash.com/photo-1594336422972-57466eb7aa27",
    thumbnailAlt: 'Mangrove forest channels with clear water reflecting twisted aerial roots and green canopy',
    duration: '8 min',
    locations: 6,
    views: '12.8K',
    highlights: ['Tidal Zones', 'Marine Life', 'Boardwalk Tour']
  },
  {
    id: 'tour-3',
    title: 'Summit to Sea Expedition',
    description: 'Journey from Mount Apo summit through all ecological zones to the coastal mangroves.',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1db10f3c8-1765151405155.png",
    thumbnailAlt: 'Panoramic mountain vista showing elevation gradient from alpine zone to lowland forest',
    duration: '18 min',
    locations: 12,
    views: '23.5K',
    highlights: ['All Zones', 'Elevation Changes', 'Species Diversity']
  },
  {
    id: 'tour-4',
    title: 'Wetland Bird Sanctuary',
    description: 'Observe migratory and resident waterbirds in their natural wetland habitat.',
    thumbnail: "https://images.unsplash.com/photo-1717984766767-cb392a90032f",
    thumbnailAlt: 'Wetland landscape with water birds wading through shallow water among lily pads at sunrise',
    duration: '10 min',
    locations: 5,
    views: '9.7K',
    highlights: ['Bird Watching', 'Seasonal Migration', 'Observation Towers']
  }];


  // Mock Data - Conservation Progress
  const conservationProjects = [
  {
    id: 'project-1',
    title: 'Jungle Trekking Trail Development',
    location: 'Primary Forest Zone',
    status: 'Completed',
    progress: 100,
    timeline: '2023-2024',
    beforeImage: "https://images.unsplash.com/photo-1711732429615-b5a58a27259d",
    beforeImageAlt: 'Degraded forest trail with muddy path showing signs of erosion and visitor impact',
    afterImage: "https://images.unsplash.com/photo-1722688010304-bdb181fbda3a",
    afterImageAlt: 'Sustainable forest boardwalk trail system winding through pristine primary rainforest habitat in PPSRNP, featuring eco-friendly elevated wooden walkway that minimizes environmental impact while providing visitors access to observe towering dipterocarp trees and endemic wildlife',
    metrics: [
    { icon: 'Trees', label: 'Trail Length', value: '2.5 km' },
    { icon: 'Users', label: 'Annual Visitors', value: '8,500' }]
  },
  {
    id: 'project-2',
    title: 'Mangrove Reforestation Initiative',
    location: 'Coastal Buffer Zone',
    status: 'In Progress',
    progress: 78,
    timeline: '2023-2025',
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_19529e6d2-1766924022919.png",
    beforeImageAlt: 'Degraded coastal area with murky water showing signs of erosion and sparse vegetation',
    afterImage: "/assets/images/Mangrove_Forest_Tour-1769667662146.jpg",
    afterImageAlt: 'Traditional wooden boats operated by local community members navigating through pristine mangrove-lined river channels with crystal-clear water and lush green mangrove forest, showcasing successful mangrove restoration and sustainable community-based tourism partnership in PPSRNP',
    metrics: [
    { icon: 'Trees', label: 'Trees Planted', value: '45,000' },
    { icon: 'Users', label: 'Community Operators', value: '35' }]
  },
  {
    id: 'project-3',
    title: 'Philippine Eagle Nest Protection',
    location: 'Lowland Forest',
    status: 'Completed',
    progress: 100,
    timeline: '2022-2024',
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1788b794e-1765151405339.png",
    beforeImageAlt: 'Unprotected eagle nest tree in forest with minimal monitoring equipment visible',
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1145e0d48-1765151405380.png",
    afterImageAlt: 'Protected eagle nest site with monitoring cameras and restricted access signage in place',
    metrics: [
    { icon: 'Bird', label: 'Active Nests', value: '12' },
    { icon: 'TrendingUp', label: 'Fledglings', value: '8' }]

  },
  {
    id: 'project-4',
    title: 'Invasive Species Removal',
    location: 'Grassland Savanna',
    status: 'In Progress',
    progress: 62,
    timeline: '2024-2026',
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1691d4c05-1765151405525.png",
    beforeImageAlt: 'Grassland area with dense overgrown vegetation and invasive species choking out native plants',
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11c917502-1764730743956.png",
    afterImageAlt: 'Restored grassland with eco-friendly wooden boardwalk trail and native vegetation returning after invasive species removal',
    metrics: [
    { icon: 'Trash2', label: 'Area Cleared', value: '850 ha' },
    { icon: 'Flower2', label: 'Native Species', value: '34' }]

  },
  {
    id: 'project-5',
    title: 'Wetland Water Quality Restoration',
    location: 'Freshwater Wetland',
    status: 'In Progress',
    progress: 55,
    timeline: '2023-2025',
    beforeImage: "https://images.unsplash.com/photo-1644691807796-66f42c6ff3ec",
    beforeImageAlt: 'Wetland with murky water showing signs of pollution and algae blooms',
    afterImage: "https://images.unsplash.com/photo-1672307183670-6b6692f49607",
    afterImageAlt: 'Clear wetland water with healthy aquatic vegetation and visible fish populations',
    metrics: [
    { icon: 'Droplet', label: 'Water Quality', value: '+45%' },
    { icon: 'Fish', label: 'Fish Species', value: '28' }]

  }];


  // Mock Data - Seasonal Timeline
  const seasonalData = [
  {
    weather: 'Cool and Dry',
    temperature: '18-24°C',
    visitorLevel: 'Moderate',
    activeSpecies: [
    {
      name: 'Philippine Eagle',
      icon: 'Bird',
      activityLevel: 'High',
      behavior: 'Nesting season begins, eagles actively defending territories',
      location: 'Lowland Dipterocarp Forest'
    },
    {
      name: 'Migratory Waterbirds',
      icon: 'Bird',
      activityLevel: 'High',
      behavior: 'Peak migration period with thousands of birds arriving',
      location: 'Freshwater Wetland Complex'
    },
    {
      name: 'Mountain Orchids',
      icon: 'Flower2',
      activityLevel: 'Medium',
      behavior: 'Early blooming species starting to flower',
      location: 'Mount Apo Summit Forest'
    }],

    specialEvents: [
    'Annual Bird Count begins',
    'Eagle nesting monitoring intensifies']

  },
  {
    weather: 'Cool and Dry',
    temperature: '19-25°C',
    visitorLevel: 'High',
    activeSpecies: [
    {
      name: 'Wildflowers',
      icon: 'Flower2',
      activityLevel: 'High',
      behavior: 'Peak blooming season with spectacular displays',
      location: 'Grassland Savanna'
    },
    {
      name: 'Philippine Tarsier',
      icon: 'Eye',
      activityLevel: 'High',
      behavior: 'Mating season, increased nocturnal activity',
      location: 'Lowland Dipterocarp Forest'
    },
    {
      name: 'Butterflies',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Mass emergence following wildflower bloom',
      location: 'All Zones'
    }],

    specialEvents: [
    'Wildflower Festival',
    'Photography workshops available']

  },
  {
    weather: 'Warm and Dry',
    temperature: '22-28°C',
    visitorLevel: 'High',
    activeSpecies: [
    {
      name: 'Hornbills',
      icon: 'Bird',
      activityLevel: 'High',
      behavior: 'Fruiting season attracts large flocks',
      location: 'Lowland Dipterocarp Forest'
    },
    {
      name: 'Flying Lemurs',
      icon: 'Squirrel',
      activityLevel: 'Medium',
      behavior: 'Young lemurs learning to glide',
      location: 'Forest Canopy'
    },
    {
      name: 'Grassland Birds',
      icon: 'Bird',
      activityLevel: 'Medium',
      behavior: 'Nesting season in grasslands',
      location: 'Grassland Savanna'
    }],

    specialEvents: [
    'Best month for summit attempts',
    'Extended viewing hours']

  },
  {
    weather: 'Hot and Dry',
    temperature: '24-30°C',
    visitorLevel: 'Moderate',
    activeSpecies: [
    {
      name: 'Reptiles',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Peak activity during warmest month',
      location: 'All Zones'
    },
    {
      name: 'Bats',
      icon: 'Moon',
      activityLevel: 'High',
      behavior: 'Fruit bat feeding frenzy on ripening figs',
      location: 'Limestone Karst Formation'
    },
    {
      name: 'Dragonflies',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Breeding season near water bodies',
      location: 'Freshwater Wetland Complex'
    }],

    specialEvents: [
    'Last month before monsoon',
    'Reduced visitor numbers']

  },
  {
    weather: 'Transition to Wet',
    temperature: '23-29°C',
    visitorLevel: 'Low',
    activeSpecies: [
    {
      name: 'Amphibians',
      icon: 'Droplet',
      activityLevel: 'High',
      behavior: 'First rains trigger mass breeding',
      location: 'Freshwater Wetland Complex'
    },
    {
      name: 'Fungi',
      icon: 'Flower2',
      activityLevel: 'High',
      behavior: 'Mushroom fruiting begins with moisture',
      location: 'Forest Floor'
    },
    {
      name: 'Tree Frogs',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Loud chorusing after evening rains',
      location: 'All Forest Zones'
    }],

    specialEvents: [
    'Monsoon season begins',
    'Some trails may close']

  },
  {
    weather: 'Wet Season',
    temperature: '22-28°C',
    visitorLevel: 'Very Low',
    activeSpecies: [
    {
      name: 'Waterfalls',
      icon: 'Droplet',
      activityLevel: 'High',
      behavior: 'Peak flow creates spectacular displays',
      location: 'Mountain Streams'
    },
    {
      name: 'Freshwater Fish',
      icon: 'Fish',
      activityLevel: 'High',
      behavior: 'Spawning season in swollen streams',
      location: 'Rivers and Streams'
    },
    {
      name: 'Mangrove Crabs',
      icon: 'Bug',
      activityLevel: 'Medium',
      behavior: 'Breeding season during high tides',
      location: 'Mangrove Coastal Zone'
    }],

    specialEvents: [
    'Limited access to some zones',
    'Waterfall photography opportunities']

  },
  {
    weather: 'Heavy Rains',
    temperature: '21-27°C',
    visitorLevel: 'Very Low',
    activeSpecies: [
    {
      name: 'Cloud Forest Species',
      icon: 'Cloud',
      activityLevel: 'High',
      behavior: 'Mosses and epiphytes thriving in moisture',
      location: 'Mount Apo Summit Forest'
    },
    {
      name: 'Leeches',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Peak activity - protective measures needed',
      location: 'All Forest Trails'
    },
    {
      name: 'Mudskippers',
      icon: 'Fish',
      activityLevel: 'Medium',
      behavior: 'Active during low tide periods',
      location: 'Mangrove Coastal Zone'
    }],

    specialEvents: [
    'Peak monsoon month',
    'Most trails closed for safety']

  },
  {
    weather: 'Wet Season',
    temperature: '22-28°C',
    visitorLevel: 'Low',
    activeSpecies: [
    {
      name: 'Endemic Orchids',
      icon: 'Flower2',
      activityLevel: 'High',
      behavior: 'Many species blooming in wet conditions',
      location: 'Limestone Karst Formation'
    },
    {
      name: 'Pitcher Plants',
      icon: 'Flower2',
      activityLevel: 'High',
      behavior: 'Traps full of water and prey',
      location: 'Mount Apo Summit Forest'
    },
    {
      name: 'Kingfishers',
      icon: 'Bird',
      activityLevel: 'Medium',
      behavior: 'Fishing in rain-swollen streams',
      location: 'All Water Bodies'
    }],

    specialEvents: [
    'Orchid blooming season',
    'Botanical tours available']

  },
  {
    weather: 'Late Wet Season',
    temperature: '22-28°C',
    visitorLevel: 'Low',
    activeSpecies: [
    {
      name: 'Migratory Raptors',
      icon: 'Bird',
      activityLevel: 'Medium',
      behavior: 'Early migrants beginning to arrive',
      location: 'Grassland Savanna'
    },
    {
      name: 'Snakes',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Active hunting during wet conditions',
      location: 'All Zones'
    },
    {
      name: 'Cicadas',
      icon: 'Bug',
      activityLevel: 'High',
      behavior: 'Loud chorusing in forest canopy',
      location: 'Lowland Dipterocarp Forest'
    }],

    specialEvents: [
    'Rainfall decreasing',
    'Some trails reopening']

  },
  {
    weather: 'Transition to Dry',
    temperature: '21-27°C',
    visitorLevel: 'Moderate',
    activeSpecies: [
    {
      name: 'Philippine Deer',
      icon: 'Squirrel',
      activityLevel: 'High',
      behavior: 'Rutting season begins',
      location: 'Grassland Savanna'
    },
    {
      name: 'Swiftlets',
      icon: 'Bird',
      activityLevel: 'High',
      behavior: 'Nesting in cave systems',
      location: 'Limestone Karst Formation'
    },
    {
      name: 'Monitor Lizards',
      icon: 'Bug',
      activityLevel: 'Medium',
      behavior: 'Basking in increasing sunshine',
      location: 'All Zones'
    }],

    specialEvents: [
    'Trails fully reopened',
    'Visitor season begins']

  },
  {
    weather: 'Cool and Dry',
    temperature: '19-25°C',
    visitorLevel: 'High',
    activeSpecies: [
    {
      name: 'Migratory Birds',
      icon: 'Bird',
      activityLevel: 'High',
      behavior: 'Peak migration continues',
      location: 'Freshwater Wetland Complex'
    },
    {
      name: 'Fruit Bats',
      icon: 'Moon',
      activityLevel: 'High',
      behavior: 'Large colonies roosting in caves',
      location: 'Limestone Karst Formation'
    },
    {
      name: 'Civets',
      icon: 'Squirrel',
      activityLevel: 'Medium',
      behavior: 'Nocturnal activity increases',
      location: 'Lowland Dipterocarp Forest'
    }],

    specialEvents: [
    'Peak tourist season',
    'Advance booking recommended']

  },
  {
    weather: 'Cool and Dry',
    temperature: '18-24°C',
    visitorLevel: 'Very High',
    activeSpecies: [
    {
      name: 'All Species',
      icon: 'Trees',
      activityLevel: 'High',
      behavior: 'Optimal viewing conditions across all zones',
      location: 'All Zones'
    },
    {
      name: 'Christmas Orchids',
      icon: 'Flower2',
      activityLevel: 'High',
      behavior: 'Special blooming period',
      location: 'Mount Apo Summit Forest'
    },
    {
      name: 'Whale Sharks',
      icon: 'Fish',
      activityLevel: 'Medium',
      behavior: 'Occasional sightings near coast',
      location: 'Coastal Waters'
    }],

    specialEvents: [
    'Holiday season peak',
    'Special conservation programs',
    'Year-end bird count']

  }];


  // Filter zones based on search and filters
  const filteredZones = useMemo(() => {
    return ecologicalZones?.filter((zone) => {
      // Search filter
      if (searchQuery && !zone?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
      !zone?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
        return false;
      }

      // Category filter
      if (filters?.category !== 'all' && zone?.category !== filters?.category) {
        return false;
      }

      // Season filter
      if (filters?.season !== 'all') {
        const seasonMap = {
          'dry': 'Nov-Apr',
          'wet': 'May-Oct'
        };
        if (zone?.bestSeason !== seasonMap?.[filters?.season] && zone?.bestSeason !== 'Year-round') {
          return false;
        }
      }

      // Accessibility filter
      if (filters?.accessibility !== 'all') {
        const accessMap = {
          'easy': [65, 100],
          'moderate': [45, 64],
          'difficult': [0, 44]
        };
        const [min, max] = accessMap?.[filters?.accessibility];
        if (zone?.accessibilityScore < min || zone?.accessibilityScore > max) {
          return false;
        }
      }

      // Conservation status filter
      if (filters?.conservationStatus?.length > 0) {
        const statusMap = {
          'excellent': [80, 100],
          'good': [60, 79],
          'moderate': [40, 59]
        };
        const matchesStatus = filters?.conservationStatus?.some((status) => {
          const [min, max] = statusMap?.[status];
          return zone?.conservationScore >= min && zone?.conservationScore <= max;
        });
        if (!matchesStatus) return false;
      }

      // Features filter
      if (filters?.features?.length > 0) {
        const hasFeature = filters?.features?.some((feature) => {
          if (feature === 'endemic') return zone?.features?.some((f) => f?.includes('Endemic'));
          if (feature === 'endangered') return zone?.keySpecies?.some((s) => s?.status?.includes('Endangered'));
          if (feature === 'research') return zone?.features?.some((f) => f?.includes('Research'));
          if (feature === 'facilities') return zone?.features?.some((f) => f?.includes('Boardwalk') || f?.includes('Observation'));
          return false;
        });
        if (!hasFeature) return false;
      }

      return true;
    });
  }, [searchQuery, filters, ecologicalZones]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      category: 'all',
      season: 'all',
      accessibility: 'all',
      conservationStatus: [],
      features: []
    });
    setSearchQuery('');
  };

  const handleExploreZone = (zone) => {
    setSelectedZone(zone);
  };

  const handleARPreview = (zone) => {
    alert(`AR Preview feature coming soon for ${zone?.name}!\n\nThis will enable mobile users to preview the zone through augmented reality before visiting.`);
  };

  const handleStartVirtualTour = (tour) => {
    alert(`Starting virtual tour: ${tour?.title}\n\nThis will launch an immersive 360° experience in a new window.`);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--color-background)] py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-4">
                Site Showcase Gallery
              </h1>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
                Explore the diverse ecological zones, virtual tours, and conservation progress across the Puerto Princesa Subterranean River National Park.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          
          {/* NEW: Showcase Categories Section */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
                Featured Showcases
              </h2>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
                Explore our specialized conservation initiatives and sustainable practices
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {showcaseCategories?.map((category) =>
              <Link
                key={category?.id}
                to={category?.path}
                className="group bg-[var(--color-card)] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:scale-105">

                  <div className="relative h-56 overflow-hidden">
                    <img
                    src={category?.image}
                    alt={category?.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="bg-[var(--color-accent)] text-[var(--color-accent-foreground)] p-2 rounded-full">
                          <Icon name={category?.icon} size={20} />
                        </div>
                        <h3 className="text-xl font-bold text-white">
                          {category?.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-[var(--color-muted-foreground)] mb-4">
                      {category?.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {category?.highlights?.map((highlight, index) =>
                    <span
                      key={index}
                      className="px-3 py-1 bg-[var(--color-accent)]/10 text-[var(--color-accent)] text-xs rounded-full">

                          {highlight}
                        </span>
                    )}
                    </div>
                    <div className="flex items-center text-[var(--color-accent)] font-semibold group-hover:gap-2 transition-all">
                      <span>Explore</span>
                      <Icon name="ArrowRight" size={18} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              )}
            </div>

            <div className="border-t border-border pt-8">
              <h3 className="text-2xl font-bold text-[var(--color-foreground)] mb-6 text-center">
                Ecological Zones
              </h3>
            </div>
          </div>

          {/* Search and View Controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Search ecological zones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)} />

            </div>
            
            <div className="flex gap-2">
              <Button
                variant={activeView === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setActiveView('grid')}>

                <Icon name="Grid3x3" size={20} />
              </Button>
              <Button
                variant={activeView === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setActiveView('list')}>

                <Icon name="List" size={20} />
              </Button>
              <Button
                variant={showComparison ? 'default' : 'outline'}
                iconName="GitCompare"
                iconPosition="left"
                onClick={() => setShowComparison(!showComparison)}>

                Compare
              </Button>
            </div>
          </div>

          {/* Filter Panel and Results */}
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters} />

            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3 space-y-8">
              {/* Results Count */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-[var(--color-foreground)]">
                  Showing {filteredZones?.length} of {ecologicalZones?.length} zones
                </p>
              </div>

              {/* Comparison Tool */}
              {showComparison &&
              <HabitatComparisonTool zones={ecologicalZones} />
              }

              {/* Zone Cards Grid */}
              <div className={`grid gap-6 ${
              activeView === 'grid' ? 'md:grid-cols-2' : 'grid-cols-1'}`
              }>
                {filteredZones?.map((zone) =>
                <EcologicalZoneCard
                  key={zone?.id}
                  zone={zone}
                  onClick={handleExploreZone}
                  onExplore={handleExploreZone}
                  onARPreview={handleARPreview} />

                )}
              </div>

              {/* No Results */}
              {filteredZones?.length === 0 &&
              <div className="text-center py-12">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-2">No zones found</h3>
                  <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
                    Try adjusting your filters or search query
                  </p>
                  <Button variant="outline" onClick={handleResetFilters}>
                    Reset Filters
                  </Button>
                </div>
              }

              {/* Virtual Tours Section */}
              <div className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-[var(--color-foreground)]">Virtual Tours</h2>
                    <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
                      Experience our zones through immersive 360° tours
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {virtualTours?.map((tour) =>
                  <VirtualTourCard
                    key={tour?.id}
                    tour={tour}
                    onStart={handleStartVirtualTour} />

                  )}
                </div>
              </div>

              {/* Seasonal Timeline */}
              <div className="mt-12">
                <SeasonalTimeline seasonalData={seasonalData} />
              </div>

              {/* Conservation Progress */}
              <div className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-[var(--color-foreground)]">Conservation in Action</h2>
                    <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
                      See the impact of our restoration and protection efforts
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {conservationProjects?.map((project) =>
                  <ConservationProgressCard key={project?.id} project={project} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Zone Detail Modal */}
        {selectedZone &&
        <ZoneDetailModal
          zone={selectedZone}
          onClose={() => setSelectedZone(null)} />

        }
      </div>
    </div>);

};

export default SiteShowcaseGallery;